import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../../components/ui/button';
import PublicNav from '../../components/PublicNav';
import { Calendar, Clock, ArrowLeft, ArrowRight } from 'lucide-react';

export default function BlogDraftStrategy() {
  return (
    <div className="min-h-screen pb-24 md:pb-0" style={{ backgroundColor: '#F5F0E6' }}>
      <Helmet>
        <title>Draft Day Strategy: Who to Pick and When | Survivor Fantasy League</title>
        <meta name="description" content="Snake draft strategy for Survivor Fantasy League. How to identify high-value castaways, what to avoid, and how to build a balanced roster." />
        <link rel="canonical" href="https://survivorfantasy.live/blog/draft-day-strategy" />
        <meta property="og:title" content="Draft Day Strategy: Who to Pick and When" />
        <meta property="og:description" content="Snake draft strategy for Survivor Fantasy League. How to identify high-value castaways, what to avoid, and how to build a balanced roster." />
        <meta property="og:url" content="https://survivorfantasy.live/blog/draft-day-strategy" />
      </Helmet>

      <PublicNav />

      {/* Article Header */}
      <header className="py-12 md:py-16" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <div className="flex items-center justify-center gap-4 text-sm mb-6" style={{ color: '#F5F0E6' }}>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              April 2026
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              5 min read
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Draft Day Strategy: Who to Pick and When
          </h1>
        </div>
      </header>

      {/* Article Content */}
      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="prose prose-lg max-w-none" style={{ color: '#4682B4', lineHeight: '1.8' }}>
            <p className="text-xl" style={{ color: '#1E3A4C' }}>
              The draft sets your entire season. Make one or two bad early picks and you're spending the rest of the season hoping everyone else's team falls apart. Here's how to approach it with a plan instead of going on vibes.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-4" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
              Know the Draft Format Before You Pick
            </h2>
            <p>
              Before you start evaluating castaways, understand how many rounds you're drafting and how many players are in your league. This determines how deep your roster goes and changes the math on when to take risks.
            </p>
            <p>
              In a 10-player league drafting 3 rounds each, there are only 30 total picks. The 30th pick has real value — everyone in that pool is a real castaway with a real shot at doing something. In a 6-player league drafting 4 rounds, you have more flexibility because you'll accumulate more depth.
            </p>
            <p>
              In a snake draft, the most punishing spots are the middle picks (3rd, 4th, 5th out of 10). The person picking first gets first overall and last overall in round one, but the middle picks never get that advantage. If you're in the middle, prioritize upside over floor.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-4" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
              Round 1: Pick the Most Likely Finalists
            </h2>
            <p>
              Your first pick should almost always be someone you genuinely believe will be at Final Tribal Council. Don't overthink it. The question isn't "who do I like" — it's "who is most likely to be sitting in those three chairs at the end?"
            </p>
            <p>
              Look at their social position in the pre-season content (cast profiles, press interviews), their physical profile (are they a challenge threat?), and whether they seem like someone other players will want to keep around.
            </p>
            <p>
              Avoid first-round picks that are polarizing, obviously threatening, or have a history of getting targeted early. High-profile names get voted out first sometimes — that's part of the show's narrative structure.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-4" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
              Round 2: Take a Shot on a Challenge Beast
            </h2>
            <p>
              Round 2 is a good time to target someone who will grind out individual immunity wins. Even if they don't make the finale, 3–4 immunity wins is 15–20 points, which is solid value for a mid-round pick.
            </p>
            <p>
              In Survivor 50 specifically, with a large cast, there will be a lot of individual challenges after the merge. A physically dominant player who stays out of strategic crossfire can pile up immunity wins quickly.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-4" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
              Round 3 and Beyond: Idol Hunters, Sleepers, and Insurance
            </h2>
            <p>
              By round 3, most of the obvious picks are gone. This is where knowledge of the cast pays off. Look for:
            </p>
            <ul>
              <li><strong style={{ color: '#FF7F50' }}>Idol hunters</strong> — players with a reputation for finding advantages. Even one successful idol play is +7 points.</li>
              <li><strong style={{ color: '#FF7F50' }}>Sleeper picks</strong> — castaways who don't have a high draft profile but have the social or strategic skills to go deep. These are the picks that win leagues.</li>
              <li><strong style={{ color: '#FF7F50' }}>Insurance picks</strong> — if your round 1 pick is obviously a target (a big personality, a returning player, a known threat), take a backup finalist in round 2 or 3.</li>
            </ul>

            <h2 className="text-2xl font-bold mt-10 mb-4" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
              Avoid These Common Mistakes
            </h2>
            <p><strong style={{ color: '#FF7F50' }}>Taking obvious targets too early.</strong> The player everyone is excited about is often the first person voted out. Survivor editors love that storyline. If someone has a big personality and an obvious threat level, factor in their real survival odds.</p>
            <p><strong style={{ color: '#FF7F50' }}>Ignoring pre-merge contestants.</strong> In a 26-person season, roughly 13 people get voted out before the merge. Drafting someone who exits pre-merge means they contribute almost nothing to your point total. Try to draft people who feel safe through the first few episodes.</p>
            <p><strong style={{ color: '#FF7F50' }}>Going for chaos picks.</strong> The wildcard who might do something crazy is entertaining but rarely valuable. Chaotic players get voted out to stabilize the game. Predict their entertainment value, not their staying power.</p>
            <p><strong style={{ color: '#FF7F50' }}>Stacking all your value in one round.</strong> If your top 2 picks both get voted out pre-merge, you're playing from behind all season. Spread your risk. Take one high-ceiling pick and one safe floor pick in the first two rounds.</p>

            <h2 className="text-2xl font-bold mt-10 mb-4" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
              How to Prep Before Draft Night
            </h2>
            <ul>
              <li>Watch the cast reveal and first impressions videos on the Survivor YouTube channel</li>
              <li>Read a few cast analysis pieces from Survivor fan sites</li>
              <li>Make a personal ranking of all 18–26 contestants before the draft, in your own order</li>
              <li>Know your top 5 cold — who you're taking if they're still available in each of your first few rounds</li>
              <li>Have a backup for each of your top picks in case they get snagged ahead of you</li>
            </ul>
            <p>
              The draft is 15 minutes of decisions that shape your whole season. The players who take it seriously almost always outperform the ones who wing it.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center py-8 px-6 rounded-2xl mt-12" style={{ backgroundColor: '#1E3A4C' }}>
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Start your league and schedule your draft
            </h3>
            <Link to="/login?tab=register">
              <Button size="lg" className="text-white px-8" style={{ backgroundColor: '#FF7F50' }}>
                Sign Up Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>

          {/* More Articles */}
          <div className="mt-12 pt-8 border-t" style={{ borderColor: '#4682B4' }}>
            <h3 className="text-xl font-bold mb-6" style={{ color: '#1E3A4C' }}>More Articles</h3>
            <div className="space-y-4">
              <Link to="/blog/how-to-win-survivor-fantasy-league" className="block p-4 rounded-lg bg-white border-2 hover:border-[#FF7F50] transition-colors" style={{ borderColor: '#4682B4' }}>
                <span className="font-medium" style={{ color: '#1E3A4C' }}>How to Win Your Survivor Fantasy League</span>
              </Link>
              <Link to="/blog/anatomy-of-a-survivor-fantasy-season" className="block p-4 rounded-lg bg-white border-2 hover:border-[#FF7F50] transition-colors" style={{ borderColor: '#4682B4' }}>
                <span className="font-medium" style={{ color: '#1E3A4C' }}>The Anatomy of a Survivor Fantasy Season</span>
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-12" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo-new.png" alt="Survivor Fantasy League" className="w-10 h-10" />
              <div>
                <span className="text-white font-bold text-lg">Survivor Fantasy League</span>
                <p className="text-xs" style={{ color: '#4682B4' }}>The Ultimate Survivor Experience</p>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm" style={{ color: '#F5F0E6' }}>
              <Link to="/" className="hover:text-[#FF7F50] transition-colors">Home</Link>
              <Link to="/how-it-works" className="hover:text-[#FF7F50] transition-colors">How It Works</Link>
              <Link to="/rules" className="hover:text-[#FF7F50] transition-colors">Rules</Link>
              <Link to="/faq" className="hover:text-[#FF7F50] transition-colors">FAQ</Link>
              <Link to="/about" className="hover:text-[#FF7F50] transition-colors">About</Link>
              <Link to="/blog" className="hover:text-[#FF7F50] transition-colors">Blog</Link>
              <Link to="/privacy" className="hover:text-[#FF7F50] transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-[#FF7F50] transition-colors">Terms</Link>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center text-sm" style={{ borderColor: '#4682B4', color: '#4682B4' }}>
            <p>© {new Date().getFullYear()} Survivor Fantasy League. Not affiliated with CBS or Survivor.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
